import styled from '@emotion/styled'
import { type ReactNode, useEffect, useRef } from 'react'
import SkuLinkedStockEditPanel from './SkuLinkedStockEditPanel'
import { useSkuModalStockEdit } from './SkuModalStockEditContext'
import { SKU_MODAL_BODY_HEIGHT } from './skuModalConstants'

const SLIDE_PANEL_WIDTH = 380
const PICKER_PANEL_WIDTH = 420
const DRAG_IGNORE = 'input, textarea, button, a, .ant-select, .ant-segmented, .ant-upload, .ant-switch, .ant-modal-close'

type SkuModalStockEditShellProps = {
  children: ReactNode
}

const SkuModalStockEditShell = ({ children }: SkuModalStockEditShellProps) => {
  const {
    contentWidth,
    editTarget,
    closeStockEdit,
    ingredientPickerOpen,
    pickerPanel,
  } = useSkuModalStockEdit()
  const stockPanelOpen = !!editTarget
  const pickerPanelOpen = ingredientPickerOpen && !!pickerPanel

  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest(DRAG_IGNORE)) return
      if (!el.querySelector('.ant-modal-header')?.contains(target)) return

      e.preventDefault()

      const mat = new DOMMatrix(el.style.transform || 'none')
      const tx0 = mat.m41
      const ty0 = mat.m42
      const mx0 = e.clientX
      const my0 = e.clientY

      el.style.userSelect = 'none'

      const onMove = (ev: MouseEvent) => {
        el.style.transform = `translate(${tx0 + ev.clientX - mx0}px, ${ty0 + ev.clientY - my0}px)`
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        el.style.userSelect = ''
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }

    el.addEventListener('mousedown', onDown)
    return () => el.removeEventListener('mousedown', onDown)
  }, [])

  return (
    <ShellRow>
      {/* ModalWrapper คือ block เดียวใน flex-flow — ขนาดไม่เปลี่ยนเมื่อ panel เปิด/ปิด */}
      <ModalWrapper
        ref={wrapperRef}
        $width={contentWidth}
      >
        {/* Left panel: absolute relative to ModalWrapper, ไม่กระทบ layout */}
        <AbsolutePanel
          $side="left"
          $open={stockPanelOpen}
          $width={SLIDE_PANEL_WIDTH}
          $modalWidth={contentWidth}
        >
          <PanelInner
            $side="left"
            $width={SLIDE_PANEL_WIDTH}
            $modalWidth={contentWidth}
          >
            {editTarget ? (
              <SkuLinkedStockEditPanel target={editTarget} onClose={closeStockEdit} />
            ) : null}
          </PanelInner>
        </AbsolutePanel>

        {children}

        {/* Right panel: absolute relative to ModalWrapper */}
        <AbsolutePanel
          $side="right"
          $open={pickerPanelOpen}
          $width={PICKER_PANEL_WIDTH}
          $modalWidth={contentWidth}
        >
          <PanelInner
            $side="right"
            $width={PICKER_PANEL_WIDTH}
            $modalWidth={contentWidth}
          >
            {pickerPanelOpen ? pickerPanel : null}
          </PanelInner>
        </AbsolutePanel>
      </ModalWrapper>
    </ShellRow>
  )
}

export default SkuModalStockEditShell

/** container ภายนอก — ใช้ flex เพื่อ center modal แต่ไม่มี item อื่นใน flow */
const ShellRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  max-width: 100vw;
  min-width: 0;
`

/**
 * ห่อ modal — position: relative เป็น anchor ให้ AbsolutePanel
 * ขนาดของ ModalWrapper ถูกกำหนดโดย .ant-modal เท่านั้น ไม่ขึ้นกับ panel
 * overflow: visible ให้ panel แสดงออกนอก modal ได้ แต่
 * เราต้องมั่นใจว่า panel ซ่อนอยู่หลัง modal ตอนปิด (ดูที่ AbsolutePanel transform)
 */
const ModalWrapper = styled.div<{ $width: number }>`
  position: relative;
  flex-shrink: 0;
  /* จำกัดความกว้างไม่ให้ล้นจอเล็ก — cap กับ viewport (เผื่อขอบรวม 16px) */
  width: min(${({ $width }) => $width}px, calc(100vw - 16px));
  min-width: 0;
  max-width: min(${({ $width }) => $width}px, calc(100vw - 16px));
  transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  .ant-modal {
    width: min(${({ $width }) => $width}px, calc(100vw - 16px)) !important;
    max-width: calc(100vw - 16px) !important;
    transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .ant-modal-header {
    cursor: move;
    user-select: none;
  }

  .ant-modal-close {
    cursor: pointer;
  }
`

/** ShellRow ต้องกว้าง 100% เพื่อไม่ให้ AbsolutePanel ทำให้เกิด horizontal scroll */

/**
 * panel ที่ลอยออกทางซ้าย/ขวาของ modal
 * - position: absolute → ไม่ขยับ modal เลยแม้แต่น้อย
 * - ใช้ transform animate แทน width เพื่อไม่ trigger layout
 */
const PANEL_GAP = 2

/**
 * ความกว้างจอขั้นต่ำที่ panel กางข้าง modal ได้โดยไม่หลุดจอ
 * modal ถูก center → แต่ละข้างเหลือ (vw - modal) / 2 ต้องพอสำหรับ panel + ขอบ 12px
 * ต่ำกว่านี้ → สลับเป็น overlay ครอบทับ modal แทน (ไม่งั้นโดนตัดขาดแบบจอ 1300px)
 */
const sideBySideMinWidth = (modalWidth: number, panelWidth: number): number =>
  modalWidth + 2 * (panelWidth + PANEL_GAP + 12)

const AbsolutePanel = styled.div<{
  $side: 'left' | 'right'
  $open: boolean
  $width: number
  $modalWidth: number
}>`
  position: absolute;
  top: 0;
  ${({ $side }) =>
    $side === 'left'
      ? `right: calc(100% + ${PANEL_GAP}px);`
      : `left: calc(100% + ${PANEL_GAP}px);`}
  width: ${({ $open, $width }) => ($open ? `${$width}px` : '0')};
  height: ${({ $open }) => ($open ? SKU_MODAL_BODY_HEIGHT : '0')};
  overflow: hidden;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transform: translateX(
    ${({ $open, $side, $width }) => {
      if ($open) return '0'
      return $side === 'left' ? `${$width}px` : `-${$width}px`
    }}
  );
  transition:
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.22s ease,
    width 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  /* จอแคบกว่าที่กางข้างได้ → ครอบทับ modal เต็มความกว้าง/ความสูงแทน (overlay) */
  @media (max-width: ${({ $modalWidth, $width }) =>
      sideBySideMinWidth($modalWidth, $width)}px) {
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    height: auto;
    width: ${({ $open }) => ($open ? '100%' : '0')};
    transform: translateX(
      ${({ $open, $side }) =>
        $open ? '0' : $side === 'left' ? '-100%' : '100%'}
    );
    z-index: 1200;
  }
`

const PanelInner = styled.div<{
  $side: 'left' | 'right'
  $width: number
  $modalWidth: number
}>`
  width: ${({ $width }) => $width}px;
  height: 100%;
  background: #fff;
  border-radius: ${({ $side }) => ($side === 'left' ? '8px 0 0 8px' : '0 8px 8px 0')};
  box-shadow: ${({ $side }) =>
    $side === 'left'
      ? '-4px 0 18px rgba(0, 0, 0, 0.12)'
      : '4px 0 18px rgba(0, 0, 0, 0.12)'};
  border: 1px solid #e8e8e8;
  border-${({ $side }) => ($side === 'left' ? 'right' : 'left')}: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* จอแคบ: เต็มความกว้าง overlay (ต้องใช้ breakpoint เดียวกับ AbsolutePanel) */
  @media (max-width: ${({ $modalWidth, $width }) =>
      sideBySideMinWidth($modalWidth, $width)}px) {
    width: 100%;
    border-radius: 8px;
    border-left: 1px solid #e8e8e8;
    border-right: 1px solid #e8e8e8;
  }
`